import React, { useEffect, useState } from 'react';
import { Accordion, Button, Card, Stack, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { PolicyCommissionInvoiceList } from '@models/PolicyCommissionInvoice';
import { AmountWithDiscount } from '@components/Base';
import { InsurancePolicy } from '@models/InsurancePolicy';

import { selectFeaturePolicyCommissionInvoice } from './selector';
import {
  policyCommissionInvoicesFetch,
  PolicyCommissionInvoicesFetchPayload,
  policyCommissionInvoiceCreate,
  PolicyCommissionInvoiceCreatePayload,
  policyCommissionInvoiceFetch,
  PolicyCommissionInvoiceFetchPayload,
  policyCommissionInvoiceUpdate,
  PolicyCommissionInvoiceUpdatePayload,
  stateUpdate,
  StateUpdatePayload,
  stateClear,
} from './action';
import { PolicyCommissionInvoiceListView } from './policy-commission-invoice-list-view';
import { PolicyCommissionInvoiceFormModal, PolicyCommissionInvoiceFormValues } from './policy-commission-invoice-form-modal';
import { PolicyCommissionInvoiceDetailsModal } from './policy-commission-invoice-details-modal';

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<InsurancePolicy, 'id' | 'uninvoicedPremium'> & { canCreate?: boolean };

const PolicyCommissionInvoiceCardBody = ({
  id: insurancePolicyId,
  uninvoicedPremium,
  canCreate = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { invoices, pagination, isLoading, isReload } = useSelector(selectFeaturePolicyCommissionInvoice);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PolicyCommissionInvoiceList | null>(null);

  const handleShow = (invoice: PolicyCommissionInvoiceList) => {
    const payload: PolicyCommissionInvoiceFetchPayload = { id: invoice.id };
    dispatch(policyCommissionInvoiceFetch(payload));
    setShowDetailsModal(true);
  }

  const handleEdit = (invoice: PolicyCommissionInvoiceList) => {
    setSelectedInvoice(invoice);
    setShowFormModal(true);
  }

  useEffect(() => {
    const payload: PolicyCommissionInvoicesFetchPayload = { insurancePolicyId, page, perPage };
    dispatch(policyCommissionInvoicesFetch(payload));
  }, [insurancePolicyId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: PolicyCommissionInvoicesFetchPayload = { insurancePolicyId, page: 1, perPage };
    dispatch(policyCommissionInvoicesFetch(payload));
  }, [isReload]);

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              Brokerage Invoices ({pagination?.total ?? 0})
            </Toggle>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <Card.Subtitle className="m-0">Uninvoiced Premium: <AmountWithDiscount finalAmount={uninvoicedPremium} skipIncluded /></Card.Subtitle>
              {(canCreate && Number(uninvoicedPremium) > 0) && (
                <Button onClick={() => setShowFormModal(true)} variant="outline-primary">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {t('common:add')}
                </Button>
              )}
            </Stack>
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <PolicyCommissionInvoiceListView
              invoices={invoices}
              pagination={pagination}
              isLoading={isLoading}
              onShow={handleShow}
              onEdit={handleEdit}
              onChangePage={setPage}
              onChangePerPage={setPerPage}
            />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <PolicyCommissionInvoiceFormModal
        show={showFormModal}
        invoice={selectedInvoice}
        onHide={() => setShowFormModal(false)}
        onSubmit={(values: PolicyCommissionInvoiceFormValues) => {
          if (selectedInvoice) {
            const payload: PolicyCommissionInvoiceUpdatePayload = { id: selectedInvoice.id, ...values };
            dispatch(policyCommissionInvoiceUpdate(payload));
            setSelectedInvoice(null);
            setShowFormModal(false);
          } else {
            const payload: PolicyCommissionInvoiceCreatePayload = { insurancePolicyId, ...values };
            dispatch(policyCommissionInvoiceCreate(payload));
            setShowFormModal(false);
          }
        }}
      />

      <PolicyCommissionInvoiceDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          const payload: StateUpdatePayload = { invoice: undefined };
          dispatch(stateUpdate(payload));
        }}
      />
    </>
  );
};

export { PolicyCommissionInvoiceCardBody };
